import React, { useMemo, useCallback, useEffect, useState } from 'react';
import moment from 'moment';
import { useQuery } from 'react-query';
import {
  Form,
  notification,
  DatePicker,
  Row,
  Input,
  Button as AntButton,
  Tag,
} from 'antd';
import { PlusOutlined, CloseOutlined } from '@ant-design/icons';
import {
  createVendor,
  updateVendor,
  getVendorListByBrokerid,
  MgBrokerVendorResponse,
} from 'services/parametersService';
import DF from 'DisplayFramework';

import {
  dateToString,
  OnlyIntegerNumber,

} from 'utils/utils2';
import DataGrid from 'components/DataGrid/DataGrid';
import { DataGridColDef } from 'components/DataGrid/DataGrid';
import ActivateDisableVendorButton from '../Components/ActivateDisableVendor';
import ButtonOperation from 'components/ButtonOperation';
import UpdateButton from 'components/UpdateButtonAction';
import SaveButtonIcon from 'components/SaveBtn';

const NEW_ACTION_ID = '';

export type BrokerVendorsTabProps = {
  mgid: number;
  tpidbroker: number;
  isCreate?: any;
  associdState?: boolean;
};

const BrokerVendorsTab = ({
  mgid,
  tpidbroker,
  associdState,
}: BrokerVendorsTabProps) => {
  const [form] = Form.useForm();
  const fieldName = 'vendors';
  const [vendors, setVendors] = useState<MgBrokerVendorResponse[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchVendors = useCallback(async () => {
    if (!tpidbroker) return;
    try {
      setLoading(true);
      const response = await getVendorListByBrokerid({ mgid, tpidbroker });
      if (response.code === '200') {
        const formattedData = (response.list || []).map((vendor: any) => ({
          ...vendor,
          _originalVendorId: vendor.vendorid, 
          startdate: vendor.startdate ? moment(vendor.startdate) : null,
          enddate: vendor.enddate ? moment(vendor.enddate) : null,
        }));
        setVendors(formattedData);
      } else {
        // Don't show error notification for 404 responses
        const is404 = (response as any).error === 'UT_404_OBJECT_NOT_FOUND' || 
                      (response as any).status === 404;
        if (!is404) {
          notification.error({
            message: response.message || 'Récupération des vendeurs',
            description: (response as any).error_description || response.description,
          });
        }
      }
    } catch (error) {
      // Don't show error notification for 404 responses
      const is404 = (error as any).error === 'UT_404_OBJECT_NOT_FOUND' || 
                    (error as any).status === 404;
      if (!is404) {
        notification.error({
          message: (error as any).message || 'Récupération des vendeurs',
          description: (error as any).error_description || (error as any).description,
        });
      }
      setVendors([]);
    } finally {
      setLoading(false);
    }
  }, [mgid, tpidbroker]);

  const { refetch } = useQuery(
    ['getVendorListByBrokerid', { tpidbroker }],
    fetchVendors,
    { 
      enabled: !!tpidbroker,
      refetchOnMount: true,
      
    }
  );

  useEffect(() => {
    if (vendors.length > 0) {
      form.setFieldsValue({ [fieldName]: vendors });
    }
  }, [vendors, form]);

  const addRow = () => {
    const newVendor: any = {
      vendorid: NEW_ACTION_ID,
      enddate: null,
      mgid,
      startdate: moment(),
      tpidbroker,
      userid: '' as any,
      vendoremail: '',
      vendormobile: '',
      vendorname: '',
      vendorstatus: 'INIT',
      vendorsurname: '',
      vendortel: '',
      _isNew: true, // Flag to identify new vendors
    };
    setVendors((prev: MgBrokerVendorResponse[]) => [newVendor, ...prev]);
  };

  const removeRow = (index: number) => {
    setVendors((prev: MgBrokerVendorResponse[]) => prev.filter((_: any, i: number) => i !== index));
  };

  const onSave = useCallback(
    async (index: number) => {
      const currentVendors = form.getFieldValue(fieldName);
      const record = currentVendors[index];
      if(!record.vendorid || record.vendorid.length === 0) {
        return notification.error({ message: 'Le code du vendeur est obligatoire' });
      }
      
      if (record.vendoremail && record.vendoremail.length > 0) {
        const emailRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)+$/;
        if (!emailRegex.test(record.vendoremail)) {
            return notification.error({ message: 'Veuillez entrer un email valide' });
        }
    }

      // Prepare request payload - remove internal flags
      const { _isNew, _originalVendorId, ...cleanRecord } = record;
      
      const vendorRequest = {
        ...cleanRecord,
        startdate: dateToString(record.startdate),
        enddate: dateToString(record.enddate),
        mgid,
        tpidbroker,
      };

      try {
        if (record._isNew) {
          // Create new vendor
          const response = await createVendor({ vendorRequest });
              if (response.code === '200') {
            notification.success({ message: 'Vendeur créé avec succès' });
                refetch();
              } else {
            // Handle non-200 responses
                notification.error({
              message: response.message || 'Erreur lors de la création',
              description: (response as any).error_description || response.description,
            });
          }
        } else {
          // Update existing vendor - use original vendorid in path, new vendorid in payload
          const response = await updateVendor({ 
            vendorid: _originalVendorId || record.vendorid, // Original ID in URL path
            vendorRequest // Payload with new vendorid
          });
              if (response.code === '200') {
            notification.success({ message: 'Vendeur mis à jour avec succès' });
                refetch();
              } else {
            // Handle non-200 responses
                notification.error({
              message: response.message || 'Erreur lors de la mise à jour',
              description: (response as any).error_description || response.description,
            });
          }
        }
      } catch (error: any) {
        // Handle exceptions (network errors, etc.)
        notification.error({
          message: 'Erreur',
          description: error?.error_description || error?.description || error?.message || 'Une erreur est survenue',
            });
      }
    },
    [form, mgid, tpidbroker, refetch]
  );

  const columns = useMemo<DataGridColDef<MgBrokerVendorResponse>[]>(
    () => [
      {
        dataIndex: 'vendorid',
        key: 'vendorid', 
        render: (_value, _record, _index) => (
          <Form.Item name={[fieldName, _index, 'vendorid']} noStyle>
            <Input maxLength={30} minLength={1} />
          </Form.Item>
        ),
        width: 140,
      },
      {
        dataIndex: 'vendorname',
        key: 'vendorname',
        render: (_value, _record, _index) => (
          <Form.Item name={[fieldName, _index, 'vendorname']} noStyle>
            <Input maxLength={100} />
          </Form.Item>
        ),
        width: 140,
      },
      {
        dataIndex: 'vendorsurname',
        render: (_value, _record, _index) => (
          <Form.Item name={[fieldName, _index, 'vendorsurname']} noStyle>
            <Input maxLength={100} style={{ width: '100%' }} />
          </Form.Item>
        ),
        width: 140,
      },

      // {
      //   dataIndex: 'mgid',
      //   render: (mgid, record) => (
      //     <MgCompanyList
      //       // style={{ border: 'none', cursor: 'pointer !important' }}
      //       value={mgid}
      //     />
      //   ),
      //   width: 230,
      // },
      {
        dataIndex: 'vendoremail',
        render: (_value, _record, _index) => (
          <Form.Item 
            name={[fieldName, _index, 'vendoremail']} 
            noStyle
            rules={[
              {
                type: 'email',
                message: 'Veuillez entrer un email valide',
              },
            ]}
          >
            <Input maxLength={100} type="email" />
          </Form.Item>
        ),
        width: 140,
      },
      {
        dataIndex: 'vendortel',
        render: (_value, _record, _index) => (
          <Form.Item name={[fieldName, _index, 'vendortel']} noStyle normalize={OnlyIntegerNumber}>
            <Input maxLength={15} style={{ width: '100%' }} />
          </Form.Item>
        ),
        width: 170,
      },
      {
        dataIndex: 'vendormobile',
        render: (_value, _record, _index) => (
          <Form.Item name={[fieldName, _index, 'vendormobile']} noStyle normalize={OnlyIntegerNumber}>
            <Input maxLength={15} min={0} style={{ width: '100%' }} />
          </Form.Item>
        ),
        width: 170,
      },
      {
        dataIndex: 'startdate',
        render: (_value, _record, _index) => (
          <Form.Item name={[fieldName, _index, 'startdate']} noStyle>
            <DatePicker />
          </Form.Item>
        ),
        width: 140,
      },
      {
        dataIndex: 'enddate',
        render: (_value, _record, _index) => (
          <Form.Item name={[fieldName, _index, 'enddate']} noStyle>
            <DatePicker />
          </Form.Item>
        ),
        width: 140,
      },
      {
        comcode: 'vendorstatus',
        dataIndex: 'vendorstatuslabel',
        render: (value, _record) => (
          <Tag
            color={
              _record.vendorstatus === 'ACTIVE'
                ? 'succes'
                : _record.vendorstatus === 'DISABLED'
                ? 'error'
                : 'default'
            }
          >
            {_record.vendorstatuslabel}
          </Tag>
        ),
        width: 100,
        fixed: 'right',
        align: 'center',
      },
      {
        key: 'actions',
        render: (_, record: any, index) =>
          record._isNew ? (
            <Row justify="center">
              <UpdateButton
                onClick={(ev) => {
                  ev.preventDefault();
                  onSave(index);
                }}
                htmlType="button"
                comcode="save"
              />
              <AntButton
                danger
                onClick={() => removeRow(index)}
                htmlType="button"
                icon={<CloseOutlined height="14px" width="14px" color="white" />}
              />
            </Row>
          ) : (
            <Row justify="center">
              <ActivateDisableVendorButton
                mgid={record.mgid}
                vendorid={record.vendorid}
                statusToUpdate={record.vendorstatus}
                size="small"
                refetch={refetch}
              />
              <SaveButtonIcon
                onClick={(ev) => {
                  ev.preventDefault();
                  onSave(index);
                }}
                htmlType="button"
                onlyIcon
              />
            </Row>
          ),
        width: 210,
        fixed: 'right',
        align: 'center',
      },

      // {
      //   key: 'remove',
      //   render: (_, record) =>
      //    ,
      //   width: 100,
      // },
    ],
    [onSave, refetch, removeRow]
  );

  const isNewItemInTable = vendors.some((v: any) => v._isNew === true);

  return (
    <DF.Provider pagcode="brokerdetail">
      <Form form={form}>
              <DataGrid
                titleComcode="listOfVendors"
          rowKey={(record, index) => `${record.vendorid}_${index}`}
                dataSource={vendors}
                columns={columns}
          isLoading={loading}
                hideFilters
                extraActions={
                  <ButtonOperation
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={addRow}
                    disabled={isNewItemInTable || associdState === true}
                    comcode="add"
                  />
                }
              />
      </Form>
    </DF.Provider>
  );
};

export default BrokerVendorsTab;
